import { Injectable, signal, inject, computed, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Observable, tap, shareReplay, finalize, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { StorageService } from './storage.service';
import {
  RefreshTokenResponse,
  AuthTokens,
  STORAGE_KEYS,
} from '../models/auth.models';
import { isJwtExpired } from '../utils/jwt.util';
import { ProfileService } from './profile.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly storage = inject(StorageService);
  private readonly profileService = inject(ProfileService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  private readonly accessToken = signal<string | null>(null);
  private readonly refreshToken = signal<string | null>(null);
  private refreshTokenRequest$: Observable<string> | null = null;

  readonly isAuthenticated = computed(() => {
    const token = this.getAccessToken();
    return !!token;
  });

  constructor() {
    this.syncTokensFromCookies();

    if (!this.isBrowser) {
      return;
    }

    window.addEventListener('focus', () => this.syncTokensFromCookies());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.syncTokensFromCookies();
      }
    });
  }

  private readAccessToken(): string | null {
    return this.storage.get<string>(STORAGE_KEYS.ACCESS_TOKEN);
  }

  private readRefreshToken(): string | null {
    return this.storage.get<string>(STORAGE_KEYS.REFRESH_TOKEN);
  }

  /**
   * Access pode sumir do cookie quando o JWT expira (Max-Age = exp).
   * Mantém o refresh em memória para o guard/interceptor renovarem a sessão.
   */
  private syncTokensFromCookies(): void {
    const access = this.readAccessToken();
    const refresh = this.readRefreshToken();

    if (access && !isJwtExpired(access)) {
      this.accessToken.set(access);
      if (refresh && !isJwtExpired(refresh)) {
        this.refreshToken.set(refresh);
      }
      return;
    }

    this.accessToken.set(null);

    if (refresh && !isJwtExpired(refresh)) {
      this.refreshToken.set(refresh);
      return;
    }

    if (this.refreshToken()) {
      this.sessionExpired();
    }
  }

  getAccessToken(): string | null {
    const fromStorage = this.readAccessToken();
    if (!fromStorage || isJwtExpired(fromStorage)) {
      this.accessToken.set(null);
      return null;
    }
    if (this.accessToken() !== fromStorage) {
      this.accessToken.set(fromStorage);
    }
    return fromStorage;
  }

  /** Sempre lê do cookie — o signal pode estar desatualizado após expiração do access. */
  getRefreshToken(): string | null {
    const fromStorage = this.readRefreshToken();
    if (!fromStorage || isJwtExpired(fromStorage)) {
      return null;
    }
    if (this.refreshToken() !== fromStorage) {
      this.refreshToken.set(fromStorage);
    }
    return fromStorage;
  }

  refreshAccessToken(): Observable<string> {
    if (this.refreshTokenRequest$) {
      return this.refreshTokenRequest$;
    }

    const token = this.getRefreshToken();
    if (!token) {
      this.sessionExpired();
      return throwError(() => new Error('Refresh token ausente ou expirado'));
    }

    this.refreshTokenRequest$ = this.http
      .post<RefreshTokenResponse>(
        `${environment.sgiApiUrl}/autenticacao/refresh`,
        { refresh_token: token },
      )
      .pipe(
        tap((response) => {
          const newRefresh = response.refresh_token ?? token;
          this.setAuthTokens({
            accessToken: response.access_token,
            refreshToken: newRefresh,
          });
        }),
        map((response) => response.access_token),
        finalize(() => {
          this.refreshTokenRequest$ = null;
        }),
        shareReplay(1)
      );

    return this.refreshTokenRequest$;
  }

  /** Filhas não encerram sessão global — redirecionam ao SGI. */
  logout(): void {
    this.profileService.clearProfile();
    this.redirectToSgiLogin();
  }

  /** Sessão inválida (refresh falhou) — limpa estado local e envia ao login do SGI. */
  sessionExpired(): void {
    this.clearLocalSession();
    this.profileService.clearProfile();
    this.redirectToSgiLogin();
  }

  setAuthTokens(tokens: AuthTokens): void {
    if (tokens.accessToken) {
      this.storage.set(STORAGE_KEYS.ACCESS_TOKEN, tokens.accessToken, true);
      this.accessToken.set(tokens.accessToken);
    }
    if (tokens.refreshToken) {
      this.storage.set(STORAGE_KEYS.REFRESH_TOKEN, tokens.refreshToken, true);
      this.refreshToken.set(tokens.refreshToken);
    }
  }

  private redirectToSgiLogin(): void {
    if (!this.isBrowser) return;
    window.location.assign(`${environment.sgiUrl}/login`);
  }

  private clearLocalSession(): void {
    this.storage.remove(STORAGE_KEYS.ACCESS_TOKEN);
    this.storage.remove(STORAGE_KEYS.REFRESH_TOKEN);
    this.accessToken.set(null);
    this.refreshToken.set(null);
  }
}
